/*eslint max-lines-per-function: ["error", 270]*/
/*eslint complexity: ["error", 23]*/
import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';

import FormRow from '../../../app/components/FormRow';
import { TooltipKey } from '../../../common/pokCore/tooltip.enum';
import { NewDatePicker } from '../../../app/components/NewDatePicker';
import {
  SurveyFrequencyEnum,
  SurveyFrequencyObj,
  ContractorTypeEnum,
  ContractorTypeObj,
} from '../../../common/pokCore/validation/enums';
import { CreateUpdateSurveyDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';
import CompanySelector from '../Selects/CompanySelector';
import EmployeeSelector from '../Selects/EmployeeSelector';
import { getOptionValues } from '../Selects/selectUtils';
import DictionarySelector from '../Selects/DictionarySelector';
import { ClientGroupSelector } from '../Selects/ClientGroupSelector';

interface SurveyBasicDataEditorProps {
  survey: CreateUpdateSurveyDto;
  propertyChange: (obj: Partial<CreateUpdateSurveyDto>) => void;
  contactTypeShortname?: string | null;
  readOnly?: boolean;
  isLocked?: boolean;
}

export const SurveyBasicDataEditor: React.FC<
  SurveyBasicDataEditorProps
> = props => {
  const { survey, propertyChange, readOnly, isLocked } = props;
  const { t, tk } = useTranslation('administrationLng');
  const [contactTypeShortname, setContactTypeShortname] = useState<
    string | null | undefined
  >(props.contactTypeShortname);

  useEffect(() => {
    setContactTypeShortname(props.contactTypeShortname);
  }, [props.contactTypeShortname]);

  const frequencyOptions = Object.entries(SurveyFrequencyEnum).map(
    ([key, value]) => ({
      value: key as SurveyFrequencyEnum,
      label: t(SurveyFrequencyObj[value]),
    }),
  );

  const contractorTypeOptions = Object.entries(ContractorTypeEnum).map(
    ([key, value]) => ({
      value: key as ContractorTypeEnum,
      label: t(ContractorTypeObj[value]),
    }),
  );

  return (
    <>
      <FormRow
        controlId="name"
        label={t(tk.common.name)}
        tooltipKey={TooltipKey.nameForClientsTooltip}
      >
        <Form.Control
          type="text"
          value={survey.name || ''}
          onChange={e => propertyChange({ name: e.target.value })}
          disabled={readOnly || isLocked}
        />
      </FormRow>

      <FormRow
        controlId="nameEn"
        label={t(tk.common.nameEn)}
        tooltipKey={TooltipKey.nameForClientsTooltip}
      >
        <Form.Control
          type="text"
          value={survey.nameEn || ''}
          onChange={e => propertyChange({ nameEn: e.target.value })}
          disabled={readOnly || isLocked}
        />
      </FormRow>

      <FormRow controlId="frequency" label={t(tk.survey.frequency)}>
        <Select
          value={
            survey.frequency
              ? frequencyOptions.find(o => o.value === survey.frequency)
              : null
          }
          onChange={option =>
            propertyChange({ frequency: option?.value || undefined })
          }
          options={frequencyOptions}
          isDisabled={readOnly || isLocked}
          isClearable
          placeholder={t(tk.component.pointOut)}
        />
      </FormRow>

      <FormRow
        controlId="contractorType"
        label={t(tk.survey.contractorType)}
        tooltipKey={TooltipKey.contractorTypeTooltip}
        tooltipSize="wide"
      >
        <Select
          value={
            survey.contractorType
              ? contractorTypeOptions.find(
                  o => o.value === survey.contractorType,
                )
              : null
          }
          onChange={option =>
            propertyChange({ contractorType: option?.value || undefined })
          }
          options={contractorTypeOptions}
          isDisabled={readOnly || isLocked}
          isClearable
          placeholder={t(tk.component.pointOut)}
        />
      </FormRow>

      {survey.contractorType === ContractorTypeEnum.Group && (
        <FormRow controlId="clientGroupId" label={t(tk.survey.clientGroup)}>
          <ClientGroupSelector
            value={survey.clientGroupId}
            clearable={true}
            readOnly={readOnly || isLocked}
            onChange={option =>
              propertyChange({ clientGroupId: option?.value ?? null })
            }
          />
        </FormRow>
      )}

      <FormRow
        controlId="validityPeriodDays"
        label={t(tk.survey.validityPeriodDays)}
        tooltipKey={TooltipKey.validityPeriodDaysTooltip}
      >
        <Form.Control
          type="number"
          value={survey.validityPeriodDays || ''}
          onChange={e =>
            propertyChange({
              validityPeriodDays: e.target.value
                ? parseInt(e.target.value, 10)
                : undefined,
            })
          }
          disabled={readOnly || isLocked}
        />
      </FormRow>

      <FormRow controlId="remind" label={t(tk.survey.remind)}>
        <Form.Check
          type="checkbox"
          className="pt-2"
          checked={survey.remind}
          onChange={e => propertyChange({ remind: e.target.checked })}
          disabled={readOnly || isLocked}
        />
      </FormRow>

      <FormRow controlId="contactType" label={t(tk.survey.contactType)}>
        <DictionarySelector
          dictionary="Typy kontaktów"
          value={survey.contactTypeId ?? undefined}
          onChange={option => {
            const shortname = option?.badge ?? null;
            propertyChange({
              contactTypeId: option?.value ?? null,
              ...(!shortname || shortname === 'DF'
                ? { sendToGeneral: false }
                : {}),
            });
            setContactTypeShortname(shortname);
          }}
          clearable={true}
          readOnly={readOnly || isLocked}
        />
      </FormRow>

      {contactTypeShortname && contactTypeShortname !== 'DF' && (
        <FormRow controlId="sendToGeneral" label={t(tk.survey.sendToGeneral)}>
          <Form.Check
            type="checkbox"
            className="pt-2"
            checked={survey.sendToGeneral ?? false}
            onChange={e => propertyChange({ sendToGeneral: e.target.checked })}
            disabled={readOnly || isLocked}
          />
        </FormRow>
      )}

      <FormRow controlId="companies" label={t(tk.common.companies)}>
        <CompanySelector
          isMulti={true}
          value={survey.companyIds}
          onChange={options => {
            propertyChange({
              companyIds: getOptionValues(options),
            });
          }}
          readOnly={readOnly || isLocked}
        />
      </FormRow>

      <FormRow controlId="employees" label={t(tk.survey.responsiblePersons)}>
        <EmployeeSelector
          isMulti={true}
          fetchForOrders={true}
          disableAutoFilter={true}
          value={survey.employeeIds}
          onChange={options => {
            propertyChange({
              employeeIds: getOptionValues(options),
            });
          }}
          readOnly={readOnly || isLocked}
        />
      </FormRow>

      <FormRow
        controlId="startDate"
        label={t(tk.survey.startDate)}
        tooltipKey={TooltipKey.startDateTooltip}
      >
        <NewDatePicker
          value={survey.startDate}
          dateChanged={date => propertyChange({ startDate: date })}
          readOnly={readOnly || isLocked}
          onClear={() => propertyChange({ startDate: null })}
        />
      </FormRow>

      <FormRow controlId="endDate" label={t(tk.survey.endDate)}>
        <NewDatePicker
          value={survey.endDate}
          dateChanged={date => propertyChange({ endDate: date })}
          onClear={() => propertyChange({ endDate: null })}
          readOnly={readOnly}
        />
      </FormRow>

      <FormRow controlId="active" label={t(tk.common.activeShe)}>
        <Form.Check
          type="checkbox"
          className="pt-2"
          checked={survey.active}
          onChange={e => propertyChange({ active: e.target.checked })}
          disabled={isLocked}
        />
      </FormRow>
    </>
  );
};
